import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { HiEye, HiPlus, HiSearch, HiDocumentText, HiCheck, HiBan, HiCash, HiArrowLeft, HiTrash, HiDownload, HiPrinter } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import Tooltip from '../components/Tooltip';
import CitySelect from '../components/CitySelect';
import CompleteReturnModal from '../components/CompleteReturnModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { formatPKR } from '../utils/currencyFormatter';
import { formatPartySearchBalanceLabel, partySearchBalanceClassName } from '../utils/partySearchBalance';
import { API } from '../config';
import axios from 'axios';
import { downloadSaleInvoicePdf } from '../utils/saleInvoicePdf';
import { getSaleItemProductCode } from '../utils/saleInvoiceDisplay';
import './Sales.css';

const EnUr = ({ en, ur, as: Tag = 'span' }) => (
  <Tag className="en-ur">
    <span className="en-ur-en">{en}</span>
    {ur ? <span className="en-ur-ur" dir="rtl" lang="ur">{ur}</span> : null}
  </Tag>
);

const Sales = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, isAdmin } = useBranch();
  const location = useLocation();
  const navigate = useNavigate();
  const { success, error } = useNotification();
  const [sales, setSales] = useState([]);
  const [saleReturns, setSaleReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReturnsList, setShowReturnsList] = useState(false);
  const [highlightedReturnIds, setHighlightedReturnIds] = useState([]);
  const [returnStatusFilter, setReturnStatusFilter] = useState(''); // '' = all, 'draft', 'completed'
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showCompleteReturnModal, setShowCompleteReturnModal] = useState(false);
  const [returnToDelete, setReturnToDelete] = useState(null);
  const [showDeleteReturnConfirm, setShowDeleteReturnConfirm] = useState(false);
  const [deletingReturn, setDeletingReturn] = useState(false);
  const [showWalkInListModal, setShowWalkInListModal] = useState(false);
  const [walkInList, setWalkInList] = useState([]);
  const [walkInListLoading, setWalkInListLoading] = useState(false);
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const [returnAccounts, setReturnAccounts] = useState([]);
  const [returnCreateAccounts, setReturnCreateAccounts] = useState([]);
  const [saleAccounts, setSaleAccounts] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [invoicePdfLoadingId, setInvoicePdfLoadingId] = useState(null);
  const printAfterOpenRef = useRef(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnFormData, setReturnFormData] = useState({
    items: [],
    reason: '',
    notes: '',
    accountId: '',
    deduction: ''
  });
  const [returnQtyErrors, setReturnQtyErrors] = useState({});
  const getTodayStr = () => new Date().toISOString().split('T')[0];
  const [searchTerm, setSearchTerm] = useState('');
  const [salesFromDate, setSalesFromDate] = useState(() => getTodayStr());
  const [salesToDate, setSalesToDate] = useState(() => getTodayStr());
  const [businesses, setBusinesses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [allSaleProducts, setAllSaleProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showOurCreationsProducts, setShowOurCreationsProducts] = useState(false);
  const [ourCreationsProducts, setOurCreationsProducts] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user.business_id || '');
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    phone: '',
    city: '',
    openingBalanceDirection: 'they_owe_you',
    openingBalanceAmount: ''
  });
  const [isWalkInCustomer, setIsWalkInCustomer] = useState(true);
  const [walkInCustomers, setWalkInCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerPhone: '',
    paymentMethod: 'cash',
    discount: 0,
    paidAmount: 0,
    accountId: '',
    paymentSplits: [{ paymentMethod: 'cash', accountId: '', amount: '' }],
    notes: '',
    items: [],
    saleDate: getTodayStr()
  });
  const [currentItem, setCurrentItem] = useState({
    productId: '',
    variantId: '',
    productName: '',
    unitType: 'piece',
    availableStock: 0,
    quantity: 1,
    unitPrice: 0,
    discount: 0
  });

  useEffect(() => {
    if (user.role === 'super_admin') {
      fetchBusinesses();
    }
  }, [user.role]);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/businesses/all`);
      const businessList = response.data.businesses || [];
      setBusinesses(businessList);
      if (!selectedBusinessId && businessList.length > 0) {
        setSelectedBusinessId(businessList[0].id);
      }
    } catch (err) {
      error('Failed to fetch businesses');
    }
  };

  useEffect(() => {
    if (selectedBusinessId) {
      fetchCustomers();
    }
  }, [selectedBusinessId, effectiveBranchId]);

  const fetchAllSaleProducts = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      const branchParam = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
      const response = await axios.get(`${API}/products/business/${businessId}${branchParam}`);
      setAllSaleProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products for sale');
      setAllSaleProducts([]);
    }
  };

  useEffect(() => {
    if (showModal && !showOurCreationsProducts) {
      fetchAllSaleProducts();
    }
  }, [showModal, showOurCreationsProducts]);

  useEffect(() => {
    if (showModal && isWalkInCustomer) {
      fetchWalkInCustomers();
    }
  }, [showModal, isWalkInCustomer]);

  useEffect(() => {
    if (!showCompleteReturnModal || !selectedReturn) return;
    const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
    if (!businessId) return;
    const branchId = selectedReturn.branch_id ?? selectedReturn.branchId ?? effectiveBranchId;
    const params = branchId ? `?branchId=${branchId}` : '';
    let cancelled = false;
    axios.get(`${API}/funds/business/${businessId}/accounts${params}`)
      .then((res) => { if (!cancelled) setReturnAccounts(res.data.accounts || []); })
      .catch((err) => { if (!cancelled) { console.error('Failed to fetch accounts for return', err); setReturnAccounts([]); } });
    return () => { cancelled = true; };
  }, [showCompleteReturnModal, selectedReturn?.id, selectedReturn?.branch_id, selectedReturn?.branchId, user?.role, user?.business_id, selectedBusinessId, effectiveBranchId]);

  useEffect(() => {
    if (!showReturnModal || !selectedSale) return;
    const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
    if (!businessId) return;
    const branchId = selectedSale.branch_id ?? selectedSale.branchId ?? effectiveBranchId;
    const params = branchId ? `?branchId=${branchId}` : '';
    let cancelled = false;
    axios.get(`${API}/funds/business/${businessId}/accounts${params}`)
      .then((res) => {
        if (!cancelled) {
          setReturnCreateAccounts(res.data.accounts || []);
          const accounts = res.data.accounts || [];
          const cash = accounts.filter(a => (a.type || a.account_type) === 'cash');
          const bank = accounts.filter(a => (a.type || a.account_type) === 'bank');
          const defaultId = cash[0]?.id ?? bank[0]?.id;
          setReturnFormData(prev => ({
            ...prev,
            accountId: defaultId != null ? String(defaultId) : ''
          }));
        }
      })
      .catch((err) => { if (!cancelled) { console.error('Failed to fetch accounts for return', err); setReturnCreateAccounts([]); } });
    return () => { cancelled = true; };
  }, [showReturnModal, selectedSale?.id, selectedSale?.branch_id, selectedSale?.branchId, user?.role, user?.business_id, selectedBusinessId, effectiveBranchId]);

  useEffect(() => {
    if (!showModal) return;
    const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
    if (!businessId) return;
    const branchId = effectiveBranchId;
    const params = branchId ? `?branchId=${branchId}` : '';
    let cancelled = false;
    axios.get(`${API}/funds/business/${businessId}/accounts${params}`)
      .then((res) => {
        if (!cancelled) {
          const accounts = res.data.accounts || [];
          setSaleAccounts(accounts);
          const cash = accounts.filter(a => (a.type || a.account_type) === 'cash');
          const defaultId = cash[0]?.id ?? accounts[0]?.id;
          if (defaultId != null) {
            setFormData(prev => {
              if (!prev.paymentSplits.length || prev.paymentSplits[0].accountId !== '') return prev;
              const next = [...prev.paymentSplits];
              next[0] = { ...next[0], accountId: String(defaultId) };
              return { ...prev, paymentSplits: next };
            });
          }
        }
      })
      .catch((err) => { if (!cancelled) { console.error('Failed to fetch accounts for sale', err); setSaleAccounts([]); } });
    return () => { cancelled = true; };
  }, [showModal, user?.role, user?.business_id, selectedBusinessId, effectiveBranchId]);

  // Handle prefill from Products page (Make Sale / Sell Selected)
  useEffect(() => {
    const prefill = location.state?.prefillProducts;
    if (!prefill || !Array.isArray(prefill) || prefill.length === 0) return;
    const items = prefill.map(p => {
      const stock = p.display_quantity ?? (p.product_type === 'unstitched' && p.variants?.length
        ? p.variants.reduce((s, v) => s + (v.inventories || []).reduce((i, inv) => i + parseFloat(inv.quantity || 0), 0), 0)
        : parseFloat(p.quantity || 0));
      const firstVariant = p.variants?.[0];
      return {
        productId: p.id,
        variantId: firstVariant?.id || null,
        productName: p.name,
        unitType: p.unit_type || 'piece',
        color: firstVariant?.color || '',
        size: firstVariant?.size || '',
        quantity: 1,
        unitPrice: parseFloat(p.sale_price || p.cost_price || 0),
        discount: 0,
        availableStock: stock
      };
    }).filter(it => it.availableStock > 0);
    if (items.length > 0) {
      setFormData(prev => ({ ...prev, items }));
      setShowModal(true);
    }
    navigate('/sales', { replace: true, state: {} });
  }, [location.state?.prefillProducts, navigate]);

  const branchIdForList = effectiveBranchId;
  useEffect(() => {
    if (branchIdForList || user?.role === 'super_admin') {
      fetchSales();
      fetchSaleReturns();
    }
  }, [branchIdForList, user?.role, returnStatusFilter, salesFromDate, salesToDate, selectedBusinessId]);

  useEffect(() => {
    const state = location.state;
    if (state?.viewReturns === true) {
      setShowReturnsList(true);
      if (Array.isArray(state.highlightReturnIds) && state.highlightReturnIds.length > 0) {
        setHighlightedReturnIds(state.highlightReturnIds);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    const saleId = location.state?.openReturnForSaleId;
    if (!saleId) return;
    let cancelled = false;
    axios.get(`${API}/sales/${saleId}`)
      .then((res) => {
        if (cancelled) return;
        const sale = res.data.sale;
        if (sale) handleInitiateReturn(sale);
        navigate(location.pathname, { replace: true, state: {} });
      })
      .catch((err) => {
        if (!cancelled) error(getErrorMessage(err));
        navigate(location.pathname, { replace: true, state: {} });
      });
    return () => { cancelled = true; };
  }, [location.state?.openReturnForSaleId, location.pathname, navigate, error]);

  useEffect(() => {
    if (highlightedReturnIds.length > 0) {
      const t = setTimeout(() => setHighlightedReturnIds([]), 8000);
      return () => clearTimeout(t);
    }
  }, [highlightedReturnIds.length]);

  const fetchSaleReturns = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      let url = `${API}/sale-returns/business/${businessId}`;
      if (returnStatusFilter) {
        url += `?status=${returnStatusFilter}`;
      }
      const response = await axios.get(url);
      setSaleReturns(response.data.sale_returns || []);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const fetchSales = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      const bid = effectiveBranchId;
      const params = new URLSearchParams();
      if (bid) params.set('branchId', bid);
      if (salesFromDate) params.set('fromDate', salesFromDate);
      if (salesToDate) params.set('toDate', salesToDate);
      const url = `${API}/sales/business/${businessId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get(url);
      setSales(response.data.sales || []);
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
      const response = await axios.get(`${API}/parties/business/${businessId}/customers${params}`);
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Failed to fetch customers');
    }
  };

  const fetchWalkInCustomers = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
      const response = await axios.get(`${API}/sales/business/${businessId}/walk-in-customers${params}`);
      setWalkInCustomers(response.data.walkInCustomers || []);
    } catch (err) {
      console.error('Failed to fetch walk-in customers');
      setWalkInCustomers([]);
    }
  };

  useEffect(() => {
    if (!showWalkInListModal) return;
    let cancelled = false;
    setWalkInListLoading(true);
    const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
    if (!businessId) {
      setWalkInListLoading(false);
      return;
    }
    const params = effectiveBranchId ? `?branchId=${effectiveBranchId}` : '';
    axios.get(`${API}/sales/business/${businessId}/walk-in-customers${params}`)
      .then((res) => {
        if (!cancelled) setWalkInList(res.data.walkInCustomers || []);
      })
      .catch(() => {
        if (!cancelled) setWalkInList([]);
      })
      .finally(() => {
        if (!cancelled) setWalkInListLoading(false);
      });
    return () => { cancelled = true; };
  }, [showWalkInListModal, user?.role, user?.business_id, selectedBusinessId, effectiveBranchId]);

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId);
      const amount = parseFloat(customerFormData.openingBalanceAmount) || 0;
      const dir = customerFormData.openingBalanceDirection;
      const openingBalance = (dir === 'they_owe_you' ? amount : dir === 'you_owe_them' ? -amount : 0);
      const payload = {
        name: customerFormData.name,
        phone: customerFormData.phone,
        city: customerFormData.city,
        openingBalance,
        isVendor: false,
        isCustomer: true,
        isTailor: false,
        isAdditional: false,
        businessId: parseInt(businessId)
      };
      if (effectiveBranchId) payload.branchId = effectiveBranchId;

      const response = await axios.post(`${API}/parties`, payload);
      success('Customer created successfully!');
      setShowAddCustomerModal(false);
      setCustomerFormData({ name: '', phone: '', city: '', openingBalanceDirection: 'they_owe_you', openingBalanceAmount: '' });
      await fetchCustomers();
      if (response.data.party) {
        setIsWalkInCustomer(false);
        setFormData(prev => ({ ...prev, customerId: response.data.party.id.toString() }));
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const searchInventory = async (query) => {
    try {
      const branchId = user.role === 'manager' ? user.branch_id : effectiveBranchId;
      if (!branchId || !query) return;
      const response = await axios.get(`${API}/inventory/branch/${branchId}/search?q=${query}`);
      setInventoryItems(response.data.inventory || []);
    } catch (err) {
      console.error('Failed to search inventory');
    }
  };

  const searchProducts = async (query) => {
    setProductSearchTerm(query);
    setShowOurCreationsProducts(false);
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      const branchParam = effectiveBranchId ? `&branchId=${effectiveBranchId}` : '';
      const response = await axios.get(`${API}/products/business/${businessId}/search?q=${query}${branchParam}`);
      setSearchResults(response.data.products || []);
    } catch (err) {
      console.error('Failed to search products');
      setSearchResults([]);
    }
  };

  const fetchOurCreationsForSale = async () => {
    try {
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      if (!businessId) return;
      const branchParam = effectiveBranchId ? `&branchId=${effectiveBranchId}` : '';
      const response = await axios.get(`${API}/products/business/${businessId}?ourCreations=true${branchParam}`);
      const products = (response.data.products || []).map(p => ({ ...p, is_our_creation: true }));
      setOurCreationsProducts(products);
      setSearchResults([]);
      setShowOurCreationsProducts(true);
    } catch (err) {
      console.error('Failed to fetch Our Creations');
      error(getErrorMessage(err));
    }
  };

  const handleViewDetails = async (saleId) => {
    try {
      const response = await axios.get(`${API}/sales/${saleId}`);
      setSelectedSale(response.data.sale);
      setShowDetailsModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const getActiveBusinessId = () => (
    user.role === 'super_admin' ? selectedBusinessId : (user.business_id || user.businessId)
  );

  const handleDownloadSaleInvoice = async (saleId) => {
    const businessId = getActiveBusinessId();
    if (!businessId) {
      error('Select a business.');
      return;
    }
    if (invoicePdfLoadingId) return;
    setInvoicePdfLoadingId(saleId);
    try {
      const [saleRes, bizRes] = await Promise.all([
        axios.get(`${API}/sales/${saleId}`),
        axios.get(`${API}/businesses/${businessId}`),
      ]);
      const sale = saleRes.data?.sale;
      const biz = bizRes.data?.business ?? bizRes.data;
      await downloadSaleInvoicePdf({
        businessName: biz?.name || 'Business',
        sale,
      });
      success('Invoice PDF downloaded.');
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setInvoicePdfLoadingId(null);
    }
  };

  const handlePrintSaleInvoice = async (saleId) => {
    try {
      const response = await axios.get(`${API}/sales/${saleId}`);
      setSelectedSale(response.data.sale);
      setShowDetailsModal(true);
      printAfterOpenRef.current = true;
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (!showDetailsModal || !selectedSale || !printAfterOpenRef.current) return undefined;
    printAfterOpenRef.current = false;
    const timer = setTimeout(() => window.print(), 200);
    return () => clearTimeout(timer);
  }, [showDetailsModal, selectedSale]);

  const handleInitiateReturn = async (sale) => {
    try {
      // Fetch sale with items to ensure we have complete data
      const response = await axios.get(`${API}/sales/${sale.id}`);
      const saleWithItems = response.data.sale;
      if (saleWithItems.can_return === false) {
        error('This sale has already been fully returned. / یہ سیل پہلے ہی مکمل واپس ہو چکی ہے۔');
        return;
      }

      setSelectedSale(saleWithItems);
      // Initialize return form with remaining (unsold-back) qty per item
      const items = (saleWithItems.items || []).map(item => {
        const v = item.variant;
        const variantLabel = (v && (v.color || v.size))
          ? [v.color, v.size].filter(Boolean).filter(p => String(p).toLowerCase() !== 'default').join(' / ') || '—'
          : '—';
        const remaining = item.remaining_quantity != null ? parseFloat(item.remaining_quantity) : parseFloat(item.quantity);
        return {
          saleItemId: item.id,
          productId: item.product_id,
          variantId: item.variant_id,
          productCode: item.product?.productCode ?? item.product?.product_code ?? '—',
          productName: item.product?.name || 'N/A',
          productDescription: item.product?.description || '',
          variantLabel,
          quantity: '',
          maxQuantity: remaining,
          unitPrice: item.unit_price,
          totalPrice: 0
        };
      }).filter(item => (parseFloat(item.maxQuantity) || 0) > 0);
      if (items.length === 0) {
        error('This sale has already been fully returned. / یہ سیل پہلے ہی مکمل واپس ہو چکی ہے۔');
        return;
      }
      setReturnFormData({
        items,
        reason: '',
        notes: '',
        accountId: '',
        deduction: ''
      });
      setReturnQtyErrors({});
      setShowReturnModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleReturnItemChange = (index, field, value) => {
    if (field === 'quantity') {
      const maxQty = parseFloat(returnFormData.items[index]?.maxQuantity) || 0;
      const qty = parseFloat(value) || 0;
      const err = (value !== '' && !Number.isNaN(qty) && qty > maxQty)
        ? `Max is ${maxQty} / زیادہ سے زیادہ ${maxQty}`
        : null;
      setReturnQtyErrors(prev => ({ ...prev, [index]: err }));
    }
    setReturnFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity') {
        const qty = parseFloat(value) || 0;
        newItems[index].totalPrice = qty * parseFloat(newItems[index].unitPrice);
      }
      return { ...prev, items: newItems };
    });
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    
    const hasQtyErrors = returnFormData.items.some(
      (item, idx) => (parseFloat(item.quantity) || 0) > (parseFloat(item.maxQuantity) || 0)
    );
    if (hasQtyErrors) {
      error('Return quantity cannot exceed max for any item. Correct the highlighted fields. / واپسی کی مقدار زیادہ سے زیادہ سے زیادہ نہیں ہو سکتی۔');
      return;
    }

    // Filter items with quantity > 0
    const returnItems = returnFormData.items.filter(item => parseFloat(item.quantity) > 0);
    
    if (returnItems.length === 0) {
      error('Please select at least one item to return / واپسی کے لیے کم از کم ایک چیز منتخب کریں');
      return;
    }

    const refundTotal = returnItems.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
    const deduction = parseFloat(returnFormData.deduction) || 0;
    if (deduction < 0) {
      error('Deduction cannot be negative.');
      return;
    }
    if (deduction > refundTotal) {
      error(`Deduction cannot exceed refund total (${formatPKR(refundTotal)}). / کٹوتی واپسی کی کل رقم سے زیادہ نہیں ہو سکتی (${formatPKR(refundTotal)})۔`);
      return;
    }

    try {
      const branchId = user.role === 'manager' ? user.branch_id : effectiveBranchId;
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      
      const payload = {
        businessId: parseInt(businessId),
        branchId: parseInt(branchId),
        saleId: selectedSale.id,
        customerId: selectedSale.customer_id || null,
        customerName: selectedSale.customer_name || null,
        returnDate: new Date().toISOString().split('T')[0],
        items: returnItems.map(item => {
          const productId = item.productId ?? item.product_id;
          const variantId = item.variantId ?? item.variant_id;
          return {
            saleItemId: item.saleItemId,
            productId: productId != null ? parseInt(productId, 10) : undefined,
            variantId: variantId != null ? parseInt(variantId, 10) : null,
            quantity: parseFloat(item.quantity),
            unitPrice: parseFloat(item.unitPrice),
            totalPrice: parseFloat(item.totalPrice),
            reason: item.reason || ''
          };
        }),
        reason: returnFormData.reason,
        notes: returnFormData.notes,
        accountId: null,
        refundDeduction: (parseFloat(returnFormData.deduction) || 0)
      };

      const response = await axios.post(`${API}/sale-returns`, payload);
      success('Return created successfully! Complete it to process refund and update inventory.');
      setShowReturnModal(false);
      fetchSales();
      fetchSaleReturns();
      const created = response.data?.sale_return;
      if (created?.id) {
        navigate('/sales', { state: { viewReturns: true, highlightReturnIds: [created.id] } });
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleCompleteReturn = (saleReturn) => {
    setSelectedReturn(saleReturn);
    setShowCompleteReturnModal(true);
  };

  const handleDeleteReturnClick = (ret) => {
    setReturnToDelete(ret);
    setShowDeleteReturnConfirm(true);
  };

  const confirmDeleteReturn = async () => {
    if (!returnToDelete || deletingReturn) return;
    setDeletingReturn(true);
    try {
      await axios.delete(`${API}/sale-returns/${returnToDelete.id}`);
      success('Sale return deleted.');
      setShowDeleteReturnConfirm(false);
      setReturnToDelete(null);
      fetchSaleReturns();
      fetchSales();
    } catch (err) {
      error(getErrorMessage(err));
    } finally {
      setDeletingReturn(false);
    }
  };

  const handleCompleteReturnSubmit = async (data) => {
    try {
      await axios.post(`${API}/sale-returns/${selectedReturn.id}/complete`, data);
      success('Return completed successfully! Refund processed and inventory updated.');
      setShowCompleteReturnModal(false);
      setSelectedReturn(null);
      fetchSaleReturns();
      fetchSales();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleConfirmSale = async (saleId) => {
    try {
      await axios.post(`${API}/sales/${saleId}/confirm`);
      success('Sale confirmed successfully!');
      fetchSales();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleCancelSale = async (saleId) => {
    try {
      await axios.post(`${API}/sales/${saleId}/cancel`);
      success('Sale cancelled');
      fetchSales();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const addItemToSale = () => {
    if (!currentItem.productId) {
      error('Please select a product');
      return;
    }

    const unitPrice = parseFloat(currentItem.unitPrice) || 0;
    const discount = parseFloat(currentItem.discount) || 0;

    if (currentItem.variants?.length > 0 && currentItem.variantQtys) {
      // Our Creation with sizes: add one line per variant that has qty > 0
      const newItems = [];
      for (const v of currentItem.variants) {
        const qty = parseFloat(currentItem.variantQtys[v.id]) || 0;
        if (qty <= 0) continue;
        const avail = parseFloat(v.available_quantity) ?? 0;
        if (avail < qty) {
          error(`Insufficient stock for ${v.size || v.color || v.sku || 'variant'}. Available: ${avail}`);
          return;
        }
        newItems.push({
          productId: currentItem.productId,
          variantId: v.id,
          productName: `${currentItem.productName} - ${v.size || v.color || v.sku || 'Variant'}`,
          unitType: currentItem.unitType,
          color: v.color || '',
          size: v.size || '',
          quantity: qty,
          unitPrice,
          discount
        });
      }
      if (newItems.length === 0) {
        error('Enter quantity for at least one size');
        return;
      }
      setFormData(prev => ({ ...prev, items: [...prev.items, ...newItems] }));
    } else {
      if (currentItem.quantity <= 0) {
        error('Please enter quantity');
        return;
      }
      if (currentItem.quantity > currentItem.availableStock) {
        error(`Insufficient stock. Available: ${currentItem.availableStock} ${currentItem.unitType}(s)`);
        return;
      }
      const item = {
        productId: currentItem.productId,
        variantId: currentItem.variantId || null,
        productName: currentItem.productName,
        unitType: currentItem.unitType,
        color: currentItem.color || '',
        size: currentItem.size || '',
        quantity: parseFloat(currentItem.quantity),
        unitPrice,
        discount
      };
      setFormData(prev => ({ ...prev, items: [...prev.items, item] }));
    }

    setCurrentItem({
      productId: '',
      variantId: '',
      productName: '',
      unitType: 'piece',
      availableStock: 0,
      quantity: 1,
      unitPrice: 0,
      discount: 0
    });
    setSearchResults([]);
    setProductSearchTerm('');
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    const itemsTotal = formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unitPrice - item.discount);
    }, 0);
    return itemsTotal - (parseFloat(formData.discount) || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasBranches) {
      error('Create at least one branch before creating sales. Go to Branches and add a branch.');
      return;
    }
    const paid = parseFloat(formData.paidAmount) || 0;
    const total = calculateTotal();
    if (isWalkInCustomer && total > 0 && paid < total - 0.01) {
      error('Walk-in customers must pay the full amount. Enter the total as Paid Amount or select a registered customer for credit.');
      return;
    }
    if (paid > 0) {
      const splitsWithAccount = formData.paymentSplits.filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0);
      const singleSplitNoAmount = formData.paymentSplits.length === 1 && formData.paymentSplits[0].accountId && (formData.paymentSplits[0].amount === '' || formData.paymentSplits[0].amount == null);
      if (!singleSplitNoAmount && splitsWithAccount.length === 0) {
        error('Select at least one account and enter amount(s) for the paid amount, or use a single account (amount will be the full paid amount).');
        return;
      }
      if (!singleSplitNoAmount) {
        const allocated = splitsWithAccount.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
        if (Math.abs(allocated - paid) > 0.01) {
          error(`Payment split total (${formatPKR(allocated)}) must equal Paid Amount (${formatPKR(paid)}).`);
          return;
        }
      }
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown to create a sale.');
      return;
    }
    if (formData.items.length === 0) {
      error('Please add at least one item');
      return;
    }

    try {
      const branchId = user.role === 'manager' ? user.branch_id : effectiveBranchId;
      const businessId = user.role === 'super_admin' ? selectedBusinessId : user.business_id;
      
      const paid = parseFloat(formData.paidAmount) || 0;
      const singleSplitNoAmount = formData.paymentSplits.length === 1 && formData.paymentSplits[0].accountId && (formData.paymentSplits[0].amount === '' || formData.paymentSplits[0].amount == null);
      let paymentSplitsPayload = null;
      if (paid > 0) {
        if (singleSplitNoAmount) {
          paymentSplitsPayload = [{ accountId: parseInt(formData.paymentSplits[0].accountId, 10), amount: paid }];
        } else {
          const validSplits = formData.paymentSplits.filter(s => s.accountId && (parseFloat(s.amount) || 0) > 0);
          if (validSplits.length > 0) {
            paymentSplitsPayload = validSplits.map(s => ({ accountId: parseInt(s.accountId, 10), amount: parseFloat(s.amount) }));
          }
        }
      }

      const payload = {
        branchId: parseInt(branchId),
        businessId: parseInt(businessId),
        customerId: formData.customerId ? parseInt(formData.customerId) : null,
        customerName: formData.customerName || null,
        customerPhone: formData.customerPhone || null,
        paymentMethod: (formData.paymentSplits[0]?.paymentMethod || formData.paymentMethod) || 'cash',
        discount: parseFloat(formData.discount) || 0,
        paidAmount: paid,
        accountId: !paymentSplitsPayload && formData.accountId ? parseInt(formData.accountId, 10) : null,
        paymentSplits: paymentSplitsPayload,
        notes: formData.notes,
        saleDate: formData.saleDate || getTodayStr(),
        items: formData.items.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount
        }))
      };

      await axios.post(`${API}/sales`, payload);
      success('Sale created successfully!');
      resetForm();
      fetchSales();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setIsWalkInCustomer(true);
    setCustomerSearchTerm('');
    setFormData({
      customerId: '',
      customerName: '',
      customerPhone: '',
      paymentMethod: 'cash',
      discount: 0,
      paidAmount: 0,
      accountId: '',
      paymentSplits: [{ paymentMethod: 'cash', accountId: '', amount: '' }],
      notes: '',
      items: [],
      saleDate: getTodayStr()
    });
    setCurrentItem({ 
      productId: '', 
      variantId: '', 
      productName: '', 
      unitType: 'piece',
      availableStock: 0,
      quantity: 1, 
      unitPrice: 0, 
      discount: 0 
    });
    setInventoryItems([]);
    setSearchResults([]);
    setAllSaleProducts([]);
    setProductSearchTerm('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { bg: '#fef3c7', color: '#b45309' },
      confirmed: { bg: '#d1fae5', color: '#047857' },
      cancelled: { bg: '#fee2e2', color: '#dc2626' },
      returned: { bg: '#e0e7ff', color: '#4f46e5' }
    };
    const style = styles[status] || styles.draft;
    return <span className="status-badge" style={{ background: style.bg, color: style.color }}>{status}</span>;
  };

  return (
    <Layout>
      <div className="sales-page">
        <div className="page-header">
          <div>
            {showReturnsList ? (
              <>
                <h1>
                  <EnUr en="Sale Returns" ur="سیل ریٹرنز" />
                </h1>
                <p className="page-subtitle">
                  <EnUr en="View and complete sale returns" ur="سیل واپسی دیکھیں اور مکمل کریں" />
                </p>
              </>
            ) : (
              <>
                <h1>Sales</h1>
                <p className="page-subtitle">Manage sales and invoices</p>
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setShowWalkInListModal(true)}
              className="create-button"
              style={{ background: '#f3f4f6', color: '#374151' }}
            >
              <HiDocumentText /> Walk-in customers
            </button>
            <button 
              onClick={() => setShowReturnsList(!showReturnsList)} 
              className="create-button"
              style={{ background: showReturnsList ? '#6d28d9' : '#f3f4f6', color: showReturnsList ? 'white' : '#374151' }}
            >
              <HiArrowLeft /> {showReturnsList ? 'View Sales / سیلز دیکھیں' : 'View Returns / ریٹرنز دیکھیں'}
            </button>
            {!showReturnsList && (
              <Tooltip text={!hasBranches ? 'Create a branch first' : 'Create new sale'} position="bottom">
                <button onClick={() => setShowModal(true)} className="create-button" disabled={!hasBranches}>
                  <HiPlus />
                  <span>New Sale</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>

        {!hasBranches && (
          <div className="banner no-branches-banner">
            Create at least one branch before creating sales. Go to <a href="/branches">Branches</a> to add a branch.
          </div>
        )}
        <div className="filters-container">
          {user.role === 'super_admin' && (
            <div className="filter-dropdown business-selector">
              <select 
                value={selectedBusinessId} 
                onChange={(e) => setSelectedBusinessId(e.target.value)}
              >
                <option value="">Select Business</option>
                {businesses.map(business => (
                  <option key={business.id} value={business.id}>{business.name}</option>
                ))}
              </select>
            </div>
          )}
          {!showReturnsList && (
            <div className="sales-date-filters">
              <button
                type="button"
                className={`sales-date-btn ${salesFromDate === getTodayStr() && salesToDate === getTodayStr() ? 'active' : ''}`}
                onClick={() => { setSalesFromDate(getTodayStr()); setSalesToDate(getTodayStr()); }}
              >
                Today
              </button>
              <label className="sales-date-label">
                <span>From</span>
                <input
                  type="date"
                  value={salesFromDate || ''}
                  onChange={(e) => setSalesFromDate(e.target.value)}
                  className="sales-date-input"
                />
              </label>
              <label className="sales-date-label">
                <span>To</span>
                <input
                  type="date"
                  value={salesToDate || ''}
                  onChange={(e) => setSalesToDate(e.target.value)}
                  className="sales-date-input"
                />
              </label>
            </div>
          )}
          <div className="search-bar">
            <HiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by invoice number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {showReturnsList ? (
          <div className="table-container">
            <div className="returns-list-header">
              <h2>
                <EnUr en="Sale Returns" ur="سیل ریٹرنز" />
              </h2>
              <div className="returns-list-filters">
                <label htmlFor="return-status-filter">
                  <EnUr en="Status:" ur="حیثیت:" />
                </label>
                <select
                  id="return-status-filter"
                  value={returnStatusFilter}
                  onChange={(e) => setReturnStatusFilter(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="">All / تمام</option>
                  <option value="draft">Draft / ڈرافٹ</option>
                  <option value="completed">Completed / مکمل</option>
                </select>
              </div>
            </div>
            <p className="returns-helper-text">
              Complete draft returns to process refund and update inventory &amp; ledger.
              <span className="en-ur-ur" dir="rtl" lang="ur">ڈرافٹ واپسی مکمل کریں تاکہ ریفنڈ، سٹاک اور لیجر اپ ڈیٹ ہو جائیں۔</span>
            </p>
            {highlightedReturnIds.length > 0 && (
              <p className="returns-created-message" role="status">
                The highlighted return below was just created.
                <span className="en-ur-ur" dir="rtl" lang="ur">نیچے نمایاں واپسی ابھی بنائی گئی ہے۔</span>
              </p>
            )}
            {saleReturns.length === 0 ? (
              <div className="empty-state">
                <p>
                  <EnUr en="No returns found." ur="کوئی واپسی نہیں ملی۔" />
                </p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th><EnUr en="Return #" ur="واپسی نمبر" /></th>
                    <th><EnUr en="Invoice #" ur="انوائس نمبر" /></th>
                    <th><EnUr en="Customer" ur="گاہک" /></th>
                    <th><EnUr en="Date" ur="تاریخ" /></th>
                    <th><EnUr en="Amount" ur="رقم" /></th>
                    <th><EnUr en="Status" ur="حیثیت" /></th>
                    <th><EnUr en="Actions" ur="اقدامات" /></th>
                  </tr>
                </thead>
                <tbody>
                  {saleReturns.map((ret) => (
                    <tr
                      key={ret.id}
                      className={highlightedReturnIds.includes(ret.id) ? 'sale-return-row-highlighted' : ''}
                    >
                      <td className="font-mono">{ret.return_number}</td>
                      <td>{ret.sale?.invoice_number || 'N/A'}</td>
                      <td>{ret.customer?.name || ret.customer_name || 'Walk-in'}</td>
                      <td>{new Date(ret.return_date).toLocaleDateString()}</td>
                      <td className="font-semibold">{formatPKR(ret.total_amount)}</td>
                      <td>{getStatusBadge(ret.status)}</td>
                      <td>
                        <div className="action-buttons">
                          {ret.status === 'draft' && (
                            <>
                              <Tooltip text="Complete return / واپسی مکمل کریں" position="top">
                                <button 
                                  type="button"
                                  className="action-btn confirm-btn" 
                                  onClick={() => handleCompleteReturn(ret)}
                                >
                                  <HiCheck />
                                </button>
                              </Tooltip>
                              <Tooltip text="Delete return / واپسی حذف کریں" position="top">
                                <button 
                                  type="button"
                                  className="action-btn delete-btn"
                                  onClick={() => handleDeleteReturnClick(ret)}
                                >
                                  <HiTrash />
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : loading ? (
          <Loader message="Loading sales..." variant="block" />
        ) : (
          <div className="table-container">
            {salesFromDate === getTodayStr() && salesToDate === getTodayStr() && (
              <h2 className="sales-list-heading">Today&apos;s sales</h2>
            )}
            {sales.length > 0 && (
              <div className="sales-period-total">
                <span className="sales-period-total-label">
                  Total sales {salesFromDate === salesToDate ? `(${salesFromDate})` : `(${salesFromDate} – ${salesToDate})`}:
                </span>
                <strong className="sales-period-total-amount">{formatPKR(sales.filter((s) => s.status === 'confirmed' || s.status === 'returned').reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0))}</strong>
              </div>
            )}
            {sales.length === 0 ? (
              <div className="empty-state">
                <p>{salesFromDate === getTodayStr() && salesToDate === getTodayStr() ? "No sales today yet." : "No sales found."}</p>
                <button onClick={() => setShowModal(true)} className="empty-state-button">
                  Create Your First Sale
                </button>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Branch</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.filter(sale => 
                    !searchTerm || sale.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())
                  ).map((sale) => (
                    <tr key={sale.id}>
                      <td className="font-mono">{sale.invoice_number}</td>
                      <td>{sale.sale_date}</td>
                      <td>{sale.customer?.name || sale.customer_name || 'Walk-in'}</td>
                      <td>{sale.branch?.name}</td>
                      <td>{sale.items?.length || 0}</td>
                      <td className="font-semibold">{formatPKR(sale.total_amount)}</td>
                      <td>{formatPKR(sale.paid_amount)}</td>
                      <td>{getStatusBadge(sale.status)}</td>
                      <td className="created-at-cell">{formatDate(sale.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <Tooltip text="View invoice" position="top">
                            <button type="button" className="action-btn view-btn" onClick={() => handleViewDetails(sale.id)}>
                              <HiEye />
                            </button>
                          </Tooltip>
                          <Tooltip text="Download invoice PDF" position="top">
                            <button
                              type="button"
                              className="action-btn invoice-pdf-btn"
                              onClick={() => handleDownloadSaleInvoice(sale.id)}
                              disabled={invoicePdfLoadingId === sale.id}
                              aria-busy={invoicePdfLoadingId === sale.id}
                            >
                              <HiDownload />
                            </button>
                          </Tooltip>
                          <Tooltip text="Print invoice" position="top">
                            <button
                              type="button"
                              className="action-btn invoice-print-btn"
                              onClick={() => handlePrintSaleInvoice(sale.id)}
                            >
                              <HiPrinter />
                            </button>
                          </Tooltip>
                          {sale.status === 'confirmed' && sale.can_return && (
                            <Tooltip text="Create return for this sale / اس سیل کی واپسی بنائیں" position="top">
                              <button
                                type="button"
                                className="action-btn return-btn"
                                onClick={() => handleInitiateReturn(sale)}
                              >
                                <HiArrowLeft />
                              </button>
                            </Tooltip>
                          )}
                          {sale.status === 'draft' && (
                            <>
                              <Tooltip text="Confirm sale" position="top">
                                <button className="action-btn confirm-btn" onClick={() => handleConfirmSale(sale.id)}>
                                  <HiCheck />
                                </button>
                              </Tooltip>
                              <Tooltip text="Cancel sale" position="top">
                                <button className="action-btn delete-btn" onClick={() => handleCancelSale(sale.id)}>
                                  <HiBan />
                                </button>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Create Sale Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <h2>New Sale</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Sale date (optional)</label>
                    <input
                      type="date"
                      value={formData.saleDate || getTodayStr()}
                      onChange={(e) => setFormData(prev => ({ ...prev, saleDate: e.target.value }))}
                    />
                    <span className="form-hint">Used for party ledger. Defaults to today if empty.</span>
                  </div>
                </div>
                {/* Customer Section */}
                <div className="form-section">
                  <h3>Customer</h3>
                  <div className="form-row customer-walkin-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={isWalkInCustomer}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsWalkInCustomer(checked);
                          setCustomerSearchTerm('');
                          if (checked) {
                            setFormData(prev => ({ ...prev, customerId: '', customerName: '', customerPhone: '' }));
                          } else {
                            setFormData(prev => ({ ...prev, customerName: '', customerPhone: '' }));
                          }
                        }}
                      />
                      <span>Is a walk-in customer</span>
                    </label>
                    {!isWalkInCustomer && (
                      <Tooltip text="Add new customer (customer only)" position="left">
                        <button
                          type="button"
                          onClick={() => setShowAddCustomerModal(true)}
                          className="btn-add-customer"
                          title="Add New Customer"
                        >
                          <HiPlus /> Add
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  {isWalkInCustomer ? (
                    <>
                      <div className="form-row">
                        <div className="form-group walk-in-phone-wrap">
                          <label>Phone number</label>
                          <input
                            type="text"
                            value={formData.customerPhone}
                            onChange={(e) => {
                              const phone = e.target.value;
                              const trimmed = (phone || '').trim();
                              const existing = walkInCustomers.find(
                                w => String(w.customer_phone || '').trim() === trimmed
                              );
                              setFormData(prev => ({
                                ...prev,
                                customerPhone: phone,
                                customerName: existing ? (existing.customer_name || prev.customerName) : prev.customerName
                              }));
                              setShowPhoneSuggestions(true);
                            }}
                            onFocus={() => (formData.customerPhone || '').trim() && setShowPhoneSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 200)}
                            placeholder="e.g. 0300-1234567 — type to see suggestions"
                            autoComplete="off"
                          />
                          {showPhoneSuggestions && (formData.customerPhone || '').trim().length > 0 && (() => {
                            const q = (formData.customerPhone || '').trim().toLowerCase();
                            const suggestions = walkInCustomers.filter(w => {
                              const p = String(w.customer_phone || '').trim().toLowerCase();
                              return p && p.includes(q);
                            }).slice(0, 8);
                            if (suggestions.length === 0) return null;
                            return (
                              <div className="walk-in-phone-suggestions">
                                {suggestions.map((w, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="walk-in-phone-suggestion-item"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      setFormData(prev => ({
                                        ...prev,
                                        customerPhone: w.customer_phone || '',
                                        customerName: w.customer_name || ''
                                      }));
                                      setShowPhoneSuggestions(false);
                                    }}
                                  >
                                    <span className="walk-in-suggestion-name">{w.customer_name || '—'}</span>
                                    <span className="walk-in-suggestion-phone">{w.customer_phone || ''}</span>
                                    {(w.sale_count != null || w.total_purchased != null) && (
                                      <span className="walk-in-suggestion-meta"> · {w.sale_count ?? 0} sale(s)</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                          <span className="form-hint">Type to see matching walk-ins; select one or enter a new number and name. Same phone in this branch = same customer name.</span>
                        </div>
                        <div className="form-group">
                          <label>Customer name</label>
                          <input
                            type="text"
                            value={formData.customerName}
                            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                            placeholder={formData.customerPhone && walkInCustomers.some(w => String(w.customer_phone || '').trim() === (formData.customerPhone || '').trim()) ? 'Existing customer' : 'e.g. Ali Ahmed (new walk-in)'}
                          />
                          {formData.customerPhone && (() => {
                            const trimmed = (formData.customerPhone || '').trim();
                            const existing = walkInCustomers.find(w => String(w.customer_phone || '').trim() === trimmed);
                            return existing ? (
                              <span className="walk-in-existing-badge">Existing walk-in · {existing.sale_count != null ? `${existing.sale_count} sale(s), ` : ''}total {formatPKR(existing.total_purchased ?? 0)}</span>
                            ) : trimmed ? (
                              <span className="form-hint">New walk-in — enter name above.</span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="customer-search-wrapper">
                      <div className="form-group customer-search-group">
                        <label>Search registered customer</label>
                        <div className="customer-search-input-wrap">
                          <HiSearch className="customer-search-icon" />
                          <input
                            type="text"
                            value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            placeholder="Search by name, phone, or city..."
                          />
                        </div>
                      </div>
                      {formData.customerId && (() => {
                        const selected = customers.find(c => c.id.toString() === formData.customerId);
                        return selected ? (
                          <div className="selected-customer-badge-wrap">
                            <div className="selected-customer-badge">
                              <span className="selected-customer-label">Selected:</span>{' '}
                              <strong>{selected.name}</strong>
                              {selected.phone && ` – ${selected.phone}`}
                              {selected.city && ` · ${selected.city}`}
                              <span className={`selected-customer-balance ${partySearchBalanceClassName(selected.balance, selected.is_shop_expense)}`}>
                                {' '}
                                · Balance: {formatPartySearchBalanceLabel(selected.balance, selected.is_shop_expense)}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn-change-customer"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, customerId: '' }));
                                setCustomerSearchTerm('');
                              }}
                            >
                              Change
                            </button>
                          </div>
                        ) : null;
                      })()}
                      {!formData.customerId && (
                        <div className="customer-search-results">
                          {(() => {
                            const q = customerSearchTerm.trim().toLowerCase();
                            const filtered = q.length >= 1
                              ? customers.filter(c => {
                                  const name = (c.name || '').toLowerCase();
                                  const phone = (c.phone || '').replace(/\s/g, '');
                                  const city = (c.city || '').toLowerCase();
                                  const qNorm = q.replace(/\s/g, '');
                                  return name.includes(q) || phone.includes(qNorm) || name.includes(qNorm) || city.includes(q);
                                })
                              : customers;
                            const list = filtered.slice(0, 25);
                            if (list.length === 0) {
                              return <div className="customer-search-no-results">{q.length >= 1 ? 'No matching customers' : 'No customers yet'}</div>;
                            }
                            return list.map(customer => (
                              <div
                                key={customer.id}
                                className="customer-search-result-item"
                                onClick={() => {
                                  setFormData(prev => ({ ...prev, customerId: customer.id.toString() }));
                                  setCustomerSearchTerm('');
                                }}
                              >
                                <div className="customer-result-primary">
                                  <span className="customer-result-name">{customer.name}</span>
                                  <div className="customer-result-meta">
                                    {customer.phone && <span className="customer-result-phone">{customer.phone}</span>}
                                    {customer.city && <span className="customer-result-city">{customer.city}</span>}
                                    <span className={`customer-result-balance ${partySearchBalanceClassName(customer.balance, customer.is_shop_expense)}`}>
                                      Balance: {formatPartySearchBalanceLabel(customer.balance, customer.is_shop_expense)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Items Section */}
                <div className="form-section">
                  <h3>Items</h3>
                  <div className="item-search-row">
                    <div className="item-search item-search-with-icon">
                      <HiSearch className="item-search-icon" aria-hidden />
                      <input
                        type="text"
                        placeholder={showOurCreationsProducts ? "Search by name, code, description, tailor, or vendor..." : "Search by product name or product code..."}
                        value={productSearchTerm}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProductSearchTerm(val);
                          if (!showOurCreationsProducts && allSaleProducts.length === 0) searchProducts(val);
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className={`our-creations-sale-btn ${showOurCreationsProducts ? 'active' : ''}`}
                      onClick={() => {
                        setShowOurCreationsProducts(!showOurCreationsProducts);
                        if (!showOurCreationsProducts) fetchOurCreationsForSale();
                        else { setOurCreationsProducts([]); setProductSearchTerm(''); }
                      }}
                    >
                      Our Creations
                    </button>
                  </div>
                  {(() => {
                    const regularList = allSaleProducts.length > 0 ? allSaleProducts : searchResults;
                    const q = (productSearchTerm || '').toLowerCase().trim();
                    const selectedId = currentItem.productId;
                    const filteredRegular = regularList.filter(p => {
                      if (p.id === selectedId) return false;
                      const stock = parseFloat(p.display_quantity ?? p.displayQuantity ?? p.quantity ?? 0) || 0;
                      if (stock <= 0) return false;
                      if (q.length < 1) return true;
                      const name = (p.name || '').toLowerCase();
                      const code = (p.product_code || '').toLowerCase();
                      return name.includes(q) || code.includes(q);
                    });
                    const list = showOurCreationsProducts
                      ? ourCreationsProducts.filter(p => {
                          if (p.id === selectedId) return false;
                          const stock = parseFloat(p.display_quantity ?? p.displayQuantity ?? p.quantity ?? 0) || 0;
                          if (stock <= 0) return false;
                          if (q.length < 1) return true;
                          const name = (p.name || '').toLowerCase();
                          const code = (p.product_code || '').toLowerCase();
                          const desc = (p.description || '').toLowerCase();
                          const vendorName = (p.vendor?.name || '').toLowerCase();
                          const tailorNames = (p.our_creations_tailor_names || '').toLowerCase();
                          const fabricVendor = (p.our_creations_fabric_vendor || '').toLowerCase();
                          return name.includes(q) || code.includes(q) || desc.includes(q) ||
                            vendorName.includes(q) || tailorNames.includes(q) || fabricVendor.includes(q);
                        })
                      : filteredRegular;
                    return list.length > 0 ? (
                    <div className="search-results">
                      {list.map(product => {
                        const stock = product.display_quantity ?? product.displayQuantity ?? product.quantity ?? 0;
                        const stockNum = parseFloat(stock);
                        return (
                        <div 
                          key={product.id} 
                          className={`search-result-item ${stockNum <= 0 ? 'out-of-stock' : ''} ${product.is_our_creation ? 'our-creation' : ''}`}
                          onClick={() => {
                            if (stockNum <= 0) {
                              error('This product is out of stock');
                              return;
                            }
                            const hasVariants = (product.variants?.length > 0);
                            const variantQtys = hasVariants
                              ? Object.fromEntries((product.variants || []).map(v => [v.id, 0]))
                              : undefined;
                            setCurrentItem({
                              ...currentItem,
                              productId: product.id,
                              variantId: hasVariants ? null : (product.variants?.[0]?.id || null),
                              productName: product.name + (product.is_our_creation || product.product_type === 'stitched' ? ' (Our Creation)' : ''),
                              unitType: product.unit_type,
                              availableStock: stockNum,
                              color: product.variants?.[0]?.color || '',
                              size: product.variants?.[0]?.size || '',
                              unitPrice: product.sale_price || product.cost_price || 0,
                              ...(hasVariants ? { variants: product.variants, variantQtys } : {})
                            });
                            setSearchResults(prev => prev.filter(p => p.id !== product.id));
                            setOurCreationsProducts(prev => prev.filter(p => p.id !== product.id));
                            setProductSearchTerm(product.name);
                          }}
                        >
                          <div className="item-main-info">
                            <span className="item-code sale-item-code">{product.product_code || '—'}</span>
                            <div className="item-name-row">
                              <span className="item-name">{product.name}</span>
                              {(product.product_type === 'unstitched' || product.unit_type === 'guz') && <span className="item-type-tag unstitched-tag">Unstitched</span>}
                              {product.is_our_creation && <span className="our-creation-tag">Our Creation</span>}
                            </div>
                          </div>
                          <div className="item-details">
                            <span className={`item-stock ${stockNum <= 0 ? 'no-stock' : ''}`}>
                              Stock: {product.unit_type === 'guz' ? stockNum.toFixed(2) : stockNum} {product.unit_type}(s)
                            </span>
                            {product.business?.name && <span className="item-business">{product.business.name}</span>}
                            <span className="item-unit-type">{product.unit_type}</span>
                            <span className="item-price-row">
                              <span className="item-cost">Cost: {(() => {
                                const cost = product.cost_price ?? product.costPrice ?? product.variants?.[0]?.cost_price ?? product.variants?.[0]?.costPrice;
                                return cost != null ? formatPKR(cost) : '—';
                              })()}</span>
                              <span className="item-price">Sale: {product.sale_price != null ? formatPKR(product.sale_price) : '—'}</span>
                            </span>
                          </div>
                        </div>
                      );})}
                    </div>
                    ) : null;
                  })()}
                  
                  {currentItem.productId && (
                    <div className="selected-product-info">
                      <div className="selected-product-header">
                        <span className="selected-name">{currentItem.productName}</span>
                        {!currentItem.variants?.length && (
                          <span className="selected-stock">
                            Available: <strong>{currentItem.availableStock}</strong> {currentItem.unitType}(s)
                          </span>
                        )}
                      </div>
                      {currentItem.variants?.length > 0 ? (
                        <>
                          <p className="form-hint">Enter quantity per size. Stock will be deducted per variant.</p>
                          <p className="form-hint variant-limit-hint">You cannot enter more than the available quantity for each size.</p>
                          <div className="variant-qty-table-wrap">
                            <table className="variant-qty-table">
                              <thead>
                                <tr>
                                  <th>Size</th>
                                  <th>Available</th>
                                  <th>Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentItem.variants.map((v) => {
                                  const available = parseFloat(v.available_quantity ?? v.availableQuantity ?? 0) || 0;
                                  return (
                                  <tr key={v.id}>
                                    <td>{v.size || v.color || v.sku || '—'}</td>
                                    <td>{available}</td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        max={available}
                                        step="1"
                                        value={(() => {
                                          const raw = currentItem.variantQtys?.[v.id];
                                          if (raw === '' || raw === undefined) return '';
                                          const num = parseFloat(raw);
                                          return isNaN(num) ? '' : Math.min(Math.max(0, num), available);
                                        })()}
                                        onChange={(e) => {
                                          const raw = e.target.value === '' ? '' : parseFloat(e.target.value);
                                          const val = raw === '' ? '' : Math.min(Math.max(0, isNaN(raw) ? 0 : raw), available);
                                          setCurrentItem(prev => ({
                                            ...prev,
                                            variantQtys: { ...(prev.variantQtys || {}), [v.id]: val === '' ? '' : val }
                                          }));
                                        }}
                                        placeholder="0"
                                        className="variant-qty-input"
                                      />
                                    </td>
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="add-item-row">
                            <div className="form-group">
                              <label>Unit Price</label>
                              <input
                                type="number"
                                step="0.01"
                                value={currentItem.unitPrice}
                                onChange={(e) => setCurrentItem({ ...currentItem, unitPrice: e.target.value })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Discount</label>
                              <input
                                type="number"
                                step="0.01"
                                value={currentItem.discount}
                                onChange={(e) => setCurrentItem({ ...currentItem, discount: e.target.value })}
                              />
                            </div>
                            <button type="button" className="add-item-btn" onClick={addItemToSale}>
                              Add Item
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="add-item-row">
                          <div className="form-group">
                            <label>Qty ({currentItem.unitType}s)</label>
                            <input
                              type="number"
                              step={currentItem.unitType === 'guz' ? '0.01' : '1'}
                              min={currentItem.unitType === 'guz' ? '0.01' : '1'}
                              max={currentItem.availableStock}
                              value={currentItem.quantity}
                              onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                            />
                            <p className="form-hint variant-limit-hint">You cannot enter more than the available quantity ({currentItem.availableStock}).</p>
                          </div>
                          <div className="form-group">
                            <label>Unit Price</label>
                            <input
                              type="number"
                              step="0.01"
                              value={currentItem.unitPrice}
                              onChange={(e) => setCurrentItem({ ...currentItem, unitPrice: e.target.value })}
                            />
                          </div>
                          <div className="form-group">
                            <label>Discount</label>
                            <input
                              type="number"
                              step="0.01"
                              value={currentItem.discount}
                              onChange={(e) => setCurrentItem({ ...currentItem, discount: e.target.value })}
                            />
                          </div>
                          <button type="button" className="add-item-btn" onClick={addItemToSale}>
                            Add Item
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {formData.items.length > 0 && (
                    <div className="items-list">
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Unit</th>
                            <th>Price</th>
                            <th>Discount</th>
                            <th>Total</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.productName}</td>
                              <td>{item.quantity}</td>
                              <td><span className="unit-badge">{item.unitType}</span></td>
                              <td>{formatPKR(item.unitPrice)}</td>
                              <td>{formatPKR(item.discount)}</td>
                              <td>{formatPKR(item.quantity * item.unitPrice - item.discount)}</td>
                              <td>
                                <button type="button" className="remove-btn" onClick={() => removeItem(index)}>×</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Payment Section */}
                <div className="form-section payment-section-sale">
                  <h3>Payment</h3>
                  {(() => {
                    const total = calculateTotal();
                    const paid = parseFloat(formData.paidAmount) || 0;
                    const allocated = formData.paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                    const debt = Math.max(0, total - paid);
                    const unallocated = Math.max(0, paid - allocated);
                    const isFullDebt = total > 0 && paid === 0;
                    const splitMatchesPaid = paid > 0 && Math.abs(allocated - paid) < 0.01;
                    return (
                      <>
                        <div className="payment-summary-bar">
                          <div className="payment-summary-main">
                            <div className="payment-summary-item sale-total">
                              <span className="payment-summary-label">Sale total</span>
                              <span className="payment-summary-value">{formatPKR(total)}</span>
                            </div>
                            <span className="payment-summary-arrow" aria-hidden>→</span>
                            <div className="payment-summary-item paid">
                              <span className="payment-summary-label">Amount received</span>
                              <span className="payment-summary-value">{formatPKR(paid)}</span>
                            </div>
                            {debt > 0 && (
                              <>
                                <span className="payment-summary-arrow" aria-hidden>+</span>
                                <div className="payment-summary-item debt">
                                  <span className="payment-summary-label">{isFullDebt ? 'Debt (full)' : 'Debt (later)'}</span>
                                  <span className="payment-summary-value">{formatPKR(debt)}</span>
                                  {!isWalkInCustomer && <span className="payment-summary-note">→ customer balance</span>}
                                </div>
                              </>
                            )}
                          </div>
                          {paid > 0 && (
                            <div className={`payment-summary-allocated ${splitMatchesPaid ? 'ok' : 'warning'}`}>
                              <span className="payment-summary-label">Split total</span>
                              <span className="payment-summary-value">{formatPKR(allocated)}</span>
                              {splitMatchesPaid ? (
                                <span className="payment-summary-tick">✓ Matches received</span>
                              ) : unallocated > 0.01 ? (
                                <span className="payment-summary-remaining">{formatPKR(unallocated)} left to allocate</span>
                              ) : null}
                            </div>
                          )}
                        </div>
                        {isFullDebt && !isWalkInCustomer && (
                          <p className="payment-full-debt-hint">No payment received — full amount will be recorded as customer balance (pay later).</p>
                        )}
                        {isWalkInCustomer && debt > 0 && (
                          <p className="payment-full-debt-hint payment-walkin-no-debt">Walk-in customers must pay in full. Set Paid Amount to {formatPKR(total)} or select a registered customer for credit.</p>
                        )}
                      </>
                    );
                  })()}
                  <div className="payment-inputs-row">
                    <div className="form-group payment-paid-group">
                      <label>Amount received (PKR)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.paidAmount}
                        onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                        placeholder="0"
                        className="payment-paid-input"
                      />
                      {calculateTotal() > 0 && (
                        <button
                          type="button"
                          className="btn-quick-fill"
                          onClick={() => setFormData(prev => ({ ...prev, paidAmount: String(calculateTotal()) }))}
                        >
                          Use full sale total
                        </button>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Overall discount (PKR)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.discount}
                        onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                      />
                    </div>
                  </div>
                  {saleAccounts.length > 0 && (parseFloat(formData.paidAmount) || 0) > 0 && (() => {
                    const paid = parseFloat(formData.paidAmount) || 0;
                    const allocated = formData.paymentSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                    const remaining = Math.max(0, paid - allocated);
                    const firstEmptyIdx = formData.paymentSplits.findIndex(s => !s.amount || parseFloat(s.amount) === 0);
                    const canFillRemaining = remaining > 0.01 && firstEmptyIdx >= 0 && formData.paymentSplits[firstEmptyIdx].accountId;
                    return (
                    <div className="payment-splits-block">
                      <div className="payment-splits-header">
                        <div>
                          <label>Where to deposit</label>
                          <p className="form-hint payment-splits-desc">Choose payment method and account for each part. Sum must equal amount received. Each account once.</p>
                        </div>
                        <div className="payment-splits-actions">
                          {canFillRemaining && (
                            <button
                              type="button"
                              className="btn-fill-remaining"
                              onClick={() => {
                                const next = [...formData.paymentSplits];
                                next[firstEmptyIdx] = { ...next[firstEmptyIdx], amount: String(remaining) };
                                setFormData({ ...formData, paymentSplits: next });
                              }}
                            >
                              Fill remaining ({formatPKR(remaining)})
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-add-split"
                            onClick={() => setFormData({ ...formData, paymentSplits: [...formData.paymentSplits, { paymentMethod: 'cash', accountId: '', amount: '' }] })}
                          >
                            <HiPlus /> Add line
                          </button>
                        </div>
                      </div>
                      <div className="payment-splits-row payment-splits-header-row">
                        <span className="payment-splits-col-label">Payment method</span>
                        <span className="payment-splits-col-label">Deposit to account</span>
                        <span className="payment-splits-col-label">Amount (PKR)</span>
                        <span />
                      </div>
                      <div className="payment-splits-list">
                        {formData.paymentSplits.map((split, idx) => {
                          const method = split.paymentMethod || 'cash';
                          const isCash = method === 'cash';
                          const selectedElsewhere = (id) => formData.paymentSplits.some((s, i) => i !== idx && s.accountId === String(id));
                          const availableForRow = (acc) => String(acc.id) === split.accountId || !selectedElsewhere(acc.id);
                          const accountsByType = isCash
                            ? saleAccounts.filter(a => (a.type || a.account_type) === 'cash').filter(availableForRow)
                            : saleAccounts.filter(a => (a.type || a.account_type) === 'bank').filter(availableForRow);
                          return (
                          <div key={idx} className="payment-split-row">
                            <select
                              className="payment-split-method"
                              value={method}
                              onChange={(e) => {
                                const next = [...formData.paymentSplits];
                                const newMethod = e.target.value;
                                const wasCash = (next[idx].paymentMethod || 'cash') === 'cash';
                                const nowCash = newMethod === 'cash';
                                next[idx] = {
                                  ...next[idx],
                                  paymentMethod: newMethod,
                                  accountId: (wasCash === nowCash) ? next[idx].accountId : ''
                                };
                                setFormData({ ...formData, paymentSplits: next });
                              }}
                              title="Payment method"
                            >
                              <option value="cash">Cash</option>
                              <option value="card">Card</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="credit">Credit</option>
                            </select>
                            <select
                              value={split.accountId}
                              onChange={(e) => {
                                const next = [...formData.paymentSplits];
                                next[idx] = { ...next[idx], accountId: e.target.value };
                                setFormData({ ...formData, paymentSplits: next });
                              }}
                              title="Deposit to account"
                            >
                              <option value="">Select account (deposit here)</option>
                              {accountsByType.map(acc => {
                                const bal = acc.current_balance ?? acc.currentBalance ?? 0;
                                const suffix = acc.bank_name ? ` (${acc.bank_name})` : '';
                                return (
                                  <option key={acc.id} value={acc.id}>{acc.name}{suffix} — {formatPKR(bal)}</option>
                                );
                              })}
                            </select>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={split.amount}
                              onChange={(e) => {
                                const next = [...formData.paymentSplits];
                                next[idx] = { ...next[idx], amount: e.target.value };
                                setFormData({ ...formData, paymentSplits: next });
                              }}
                              placeholder={formData.paymentSplits.length === 1 ? 'Full paid amount' : '0'}
                            />
                            {formData.paymentSplits.length > 1 && (
                              <button
                                type="button"
                                className="btn-remove-split"
                                onClick={() => {
                                  const next = formData.paymentSplits.filter((_, i) => i !== idx);
                                  setFormData({ ...formData, paymentSplits: next.length ? next : [{ paymentMethod: 'cash', accountId: '', amount: '' }] });
                                }}
                                aria-label="Remove"
                              >
                                <HiBan />
                              </button>
                            )}
                          </div>
                          );
                        })}
                      </div>
                      {formData.paymentSplits.length === 1 && formData.paymentSplits[0].accountId && (parseFloat(formData.paidAmount) || 0) > 0 && (
                        <p className="form-hint">Single account: leave amount empty to use full received amount.</p>
                      )}
                    </div>
                    );
                  })()}
                  <div className="payment-sale-total-section">
                    <span className="total-label">Sale total</span>
                    <span className="total-amount">{formatPKR(calculateTotal())}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={resetForm}>Cancel</button>
                  <button type="submit">Create Sale</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Walk-in customers list modal */}
        {showWalkInListModal && (
          <div className="modal-overlay" onClick={() => setShowWalkInListModal(false)}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Walk-in customers</h2>
                <button type="button" className="btn-close" onClick={() => setShowWalkInListModal(false)} aria-label="Close">×</button>
              </div>
              <div className="modal-body">
                <p className="form-hint" style={{ marginBottom: 16 }}>Names and purchasing summary for customers who did not use a registered account.</p>
                {walkInListLoading ? (
                  <Loader message="Loading walk-in customers…" variant="block" />
                ) : walkInList.length === 0 ? (
                  <p className="empty-state">No walk-in customers found.</p>
                ) : (
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Phone</th>
                          <th># Sales</th>
                          <th>Total purchased</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walkInList.map((w, idx) => (
                          <tr key={idx}>
                            <td>{w.customer_name || '—'}</td>
                            <td>{w.customer_phone || '—'}</td>
                            <td>{w.sale_count ?? 0}</td>
                            <td>{formatPKR(w.total_purchased ?? 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowWalkInListModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Add Customer Modal */}
        {showAddCustomerModal && (
          <div className="modal-overlay" onClick={() => setShowAddCustomerModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add New Customer</h2>
                <button className="btn-close" onClick={() => setShowAddCustomerModal(false)}>×</button>
              </div>
              <form onSubmit={handleCreateCustomer}>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Customer Name *</label>
                    <input
                      type="text"
                      value={customerFormData.name}
                      onChange={(e) => setCustomerFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Ali Ahmed"
                      required
                    />
                  </div>
                    <div className="form-row">
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="text"
                        value={customerFormData.phone}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="e.g. 0300-1234567"
                      />
                    </div>
                    <div className="form-group">
                      <label>City *</label>
                      <CitySelect
                        value={customerFormData.city}
                        onChange={(city) => setCustomerFormData(prev => ({ ...prev, city }))}
                        required
                        placeholder="Select city"
                        className="form-input"
                      />
                    </div>
                  </div>
                  <div className="form-group opening-balance-group">
                    <label>Opening Balance</label>
                    <div className="opening-balance-options">
                      <label className={`opening-balance-option ${customerFormData.openingBalanceDirection === 'they_owe_you' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="customerOpeningBalanceDirection"
                          checked={customerFormData.openingBalanceDirection === 'they_owe_you'}
                          onChange={() => setCustomerFormData(prev => ({ ...prev, openingBalanceDirection: 'they_owe_you' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Received</span>
                        </span>
                      </label>
                      <label className={`opening-balance-option ${customerFormData.openingBalanceDirection === 'you_owe_them' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="customerOpeningBalanceDirection"
                          checked={customerFormData.openingBalanceDirection === 'you_owe_them'}
                          onChange={() => setCustomerFormData(prev => ({ ...prev, openingBalanceDirection: 'you_owe_them' }))}
                        />
                        <span className="option-text">
                          <span className="option-label">Payment Made</span>
                        </span>
                      </label>
                    </div>
                    <div className="opening-balance-amount-row">
                      <label className="amount-label">Amount (PKR)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={customerFormData.openingBalanceAmount}
                        onChange={(e) => setCustomerFormData(prev => ({ ...prev, openingBalanceAmount: e.target.value }))}
                        placeholder="0.00"
                        className="opening-balance-amount-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={() => setShowAddCustomerModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Add Customer
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Sale Details Modal */}
        {showDetailsModal && selectedSale && (
          <div className="modal-overlay sales-invoice-overlay" onClick={() => setShowDetailsModal(false)}>
            <div id="sales-invoice-print-area" className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <div className="sales-invoice-actions no-print">
                <button type="button" className="btn-primary" onClick={() => window.print()}>
                  <HiPrinter /> Print
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => handleDownloadSaleInvoice(selectedSale.id)}
                  disabled={invoicePdfLoadingId === selectedSale.id}
                >
                  <HiDownload /> {invoicePdfLoadingId === selectedSale.id ? 'Preparing…' : 'Download PDF'}
                </button>
              </div>
              <div className="invoice-top invoice-top-open">
                <div className="invoice-header-row">
                  <h2 className="invoice-title">Invoice</h2>
                  <span className="invoice-number">{selectedSale.invoice_number}</span>
                  {getStatusBadge(selectedSale.status)}
                </div>
                <div className="invoice-info-grid">
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Date</span>
                    <span className="invoice-info-value">{selectedSale.sale_date}</span>
                  </div>
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Branch</span>
                    <span className="invoice-info-value">{selectedSale.branch?.name ?? '—'}</span>
                  </div>
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Customer</span>
                    <span className="invoice-info-value">{selectedSale.customer?.name || selectedSale.customer_name || 'Walk-in'}</span>
                  </div>
                  <div className="invoice-info-item">
                    <span className="invoice-info-label">Phone</span>
                    <span className="invoice-info-value">{(selectedSale.customer?.phone || selectedSale.customer_phone) ?? '—'}</span>
                  </div>
                </div>
              </div>

              <div className="invoice-items">
                <table>
                  <thead>
                    <tr>
                      <th>Product Code</th>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSale.items?.map((item, index) => (
                      <tr key={index}>
                        <td className="invoice-product-code">{getSaleItemProductCode(item)}</td>
                        <td>{item.product?.name}</td>
                        <td>{[item.variant?.color, item.variant?.size].filter(Boolean).filter(p => String(p).toLowerCase() !== 'default').join(' / ') || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatPKR(item.unit_price)}</td>
                        <td>{formatPKR(item.discount)}</td>
                        <td>{formatPKR(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="invoice-summary">
                <div><span>Subtotal:</span> <span>{formatPKR(selectedSale.subtotal)}</span></div>
                <div><span>Discount:</span> <span>{formatPKR(selectedSale.discount)}</span></div>
                <div><span>Tax:</span> <span>{formatPKR(selectedSale.tax)}</span></div>
                <div className="total"><span>Total:</span> <span>{formatPKR(selectedSale.total_amount)}</span></div>
                <div><span>Paid:</span> <span>{formatPKR(selectedSale.paid_amount)}</span></div>
                <div className="balance">
                  <span>Balance:</span> 
                  <span>{formatPKR(parseFloat(selectedSale.total_amount) - parseFloat(selectedSale.paid_amount))}</span>
                </div>
              </div>

              <div className="modal-actions no-print">
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {selectedSale.status === 'confirmed' && selectedSale.can_return && (
                    <button 
                      className="btn-primary" 
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleInitiateReturn(selectedSale);
                      }}
                    >
                      <HiArrowLeft /> Return Items / اشیاء واپس کریں
                    </button>
                  )}
                  <button onClick={() => setShowDetailsModal(false)}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Return Modal */}
        {showReturnModal && selectedSale && (() => {
          const refundTotal = returnFormData.items.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
          const deduction = parseFloat(returnFormData.deduction) || 0;
          const amountToRefund = Math.max(0, refundTotal - deduction);
          return (
            <div className="modal-overlay" onClick={() => { setReturnQtyErrors({}); setShowReturnModal(false); }}>
              <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <h2>
                  <EnUr en={`Return Items — Invoice ${selectedSale.invoice_number}`} ur={`اشیاء واپس کریں — انوائس ${selectedSale.invoice_number}`} />
                </h2>
                <form onSubmit={handleReturnSubmit}>
                  <div className="form-section">
                    <h3>
                      <EnUr en="Select items to return" ur="واپسی کے لیے اشیاء منتخب کریں" />
                    </h3>
                    <div className="return-items-table-wrap">
                      <table className="return-items-table">
                        <thead>
                          <tr>
                            <th><EnUr en="Product Code" ur="پروڈکٹ کوڈ" /></th>
                            <th><EnUr en="Name / Description" ur="نام / تفصیل" /></th>
                            <th><EnUr en="Variant" ur="ویریئنٹ" /></th>
                            <th className="col-num"><EnUr en="Max" ur="زیادہ سے زیادہ" /></th>
                            <th className="col-num"><EnUr en="Return Qty" ur="واپسی کی مقدار" /></th>
                            <th className="col-num"><EnUr en="Unit Price" ur="یونٹ قیمت" /></th>
                            <th className="col-num"><EnUr en="Total" ur="کل" /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {returnFormData.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.productCode ?? '—'}</td>
                              <td>
                                <div>{item.productName}</div>
                                {item.productDescription ? (
                                  <span className="return-item-description en-ur-ur" dir="rtl" lang="ur">
                                    {item.productDescription}
                                  </span>
                                ) : null}
                              </td>
                              <td>{item.variantLabel ?? '—'}</td>
                              <td className="col-num">{item.maxQuantity}</td>
                              <td className="col-num">
                                <input
                                  type="number"
                                  min="0"
                                  max={item.maxQuantity}
                                  step="0.01"
                                  value={item.quantity ?? ''}
                                  onChange={(e) => handleReturnItemChange(index, 'quantity', e.target.value)}
                                  className={`return-qty-input ${returnQtyErrors[index] ? 'input-error' : ''}`}
                                  aria-invalid={!!returnQtyErrors[index]}
                                />
                                {returnQtyErrors[index] && (
                                  <div className="return-qty-error">{returnQtyErrors[index]}</div>
                                )}
                              </td>
                              <td className="col-num">{formatPKR(item.unitPrice)}</td>
                              <td className="col-num font-semibold">{formatPKR(item.totalPrice || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="return-refund-total">
                      <EnUr en="Refund total:" ur="واپسی کی کل رقم:" />
                      <strong>{formatPKR(refundTotal)}</strong>
                    </div>
                    <div className="return-deduction-row">
                      <div className="form-group return-deduction-group">
                        <label htmlFor="return-deduction">
                          <EnUr en="Deduct from refund (PKR)" ur="واپسی سے کٹوتی (روپے)" />
                        </label>
                        <input
                          id="return-deduction"
                          type="number"
                          min="0"
                          step="0.01"
                          value={returnFormData.deduction}
                          onChange={(e) => setReturnFormData({ ...returnFormData, deduction: e.target.value })}
                          placeholder="0 (e.g. restocking fee)"
                          className="return-deduction-input"
                        />
                        <span className="form-hint">
                          Optional. Deduct an amount (e.g. restocking fee) from the refund.
                          <span className="en-ur-ur" dir="rtl" lang="ur">اختیاری۔ واپسی کی رقم سے کٹوتی کریں (مثلاً ریسٹاک فیس)۔</span>
                        </span>
                      </div>
                      <div className="return-amount-to-refund">
                        <EnUr en="Amount to refund:" ur="واپس کی جانے والی رقم:" />
                        <strong>{formatPKR(amountToRefund)}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="form-section">
                    <p className="form-hint" style={{ marginBottom: 8 }}>
                      Select the refund account when you complete the return (Sales → View Returns → Complete).
                      <span className="en-ur-ur" dir="rtl" lang="ur">واپسی مکمل کرتے وقت ریفنڈ اکاؤنٹ منتخب کریں (سیلز → ویو ریٹرنز → مکمل)۔</span>
                    </p>
                    <div className="form-group">
                      <label>
                        <EnUr en="Return reason" ur="واپسی کی وجہ" />
                      </label>
                      <textarea
                        value={returnFormData.reason}
                        onChange={(e) => setReturnFormData({ ...returnFormData, reason: e.target.value })}
                        placeholder="General reason for return... / واپسی کی وجہ"
                        rows="2"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        <EnUr en="Notes" ur="نوٹس" />
                      </label>
                      <textarea
                        value={returnFormData.notes}
                        onChange={(e) => setReturnFormData({ ...returnFormData, notes: e.target.value })}
                        placeholder="Additional notes... / اضافی نوٹس"
                        rows="2"
                      />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button type="button" onClick={() => { setReturnQtyErrors({}); setShowReturnModal(false); }}>
                      Cancel / منسوخ
                    </button>
                    <button type="submit" className="btn-primary">Create Return / واپسی بنائیں</button>
                  </div>
                </form>
              </div>
            </div>
          );
        })()}

        {/* Complete Return Modal */}
        <CompleteReturnModal
          isOpen={showCompleteReturnModal}
          onClose={() => {
            setShowCompleteReturnModal(false);
            setSelectedReturn(null);
          }}
          onConfirm={handleCompleteReturnSubmit}
          returnData={selectedReturn}
          returnType="sale"
          accounts={returnAccounts}
          isRegisteredCustomer={!!(selectedReturn?.customer_id ?? selectedReturn?.customerId)}
        />

        <ConfirmationModal
          isOpen={showDeleteReturnConfirm}
          onClose={() => { setShowDeleteReturnConfirm(false); setReturnToDelete(null); }}
          onConfirm={confirmDeleteReturn}
          title="Delete sale return? / سیل واپسی حذف کریں؟"
          message={returnToDelete ? `Are you sure you want to delete return ${returnToDelete.return_number || `#${returnToDelete.id}`}? This cannot be undone. / کیا آپ واپسی ${returnToDelete.return_number || `#${returnToDelete.id}`} حذف کرنا چاہتے ہیں؟ یہ واپس نہیں ہو سکے گی۔` : ''}
          confirmText={deletingReturn ? 'Deleting… / حذف ہو رہا ہے…' : 'Delete / حذف کریں'}
          cancelText="Cancel / منسوخ"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default Sales;
